/**
 * Role Modal Component
 * Add/Edit roles with permission matrix
 */

import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { Shield, Lock, Check, X } from 'lucide-react';
import Modal from '../shared/Modal';
import { api } from '../../services/api';
import { useAuthStore } from '@store/authStore';
import { useDataSourceStore } from '@store/dataSourceStore';

interface Permission {
  resource: string;
  actions: string[];
}

interface Role {
  _id: string;
  name: string;
  slug: string;
  description?: string;
  level: number;
  permissions: Permission[];
  defaultLandingPage?: string;
  companyId?: string;
}

interface RoleModalProps {
  isOpen: boolean;
  onClose: () => void;
  role?: Role;
}

// Available resources and actions
const RESOURCES = [
  'users',
  'roles',
  'companies',
  'settings',
  'analytics',
  'funnel',
  'sessions',
  'attribution',
  'roas',
  'email-campaigns',
  'email-logs',
  'getresponse',
  'contacts',
  'eshop',
  'eshop-customers',
  'eshop-carts',
  'eshop-consultations',
  'data-sources',
  'betterstack',
  'ai-insights',
  'ai-chat',
  'dashboards',
  'reports',
  'profile',
  '*', // All resources
];

const ACTIONS = ['create', 'read', 'update', 'delete', 'export', 'sync', 'send', 'configure', 'generate', 'query', 'analyze', '*'];

const PERMISSION_PRESETS = {
  admin: {
    name: 'Admin (Full Access)',
    permissions: [
      { resource: 'users', actions: ['create', 'read', 'update', 'delete'] },
      { resource: 'roles', actions: ['create', 'read', 'update', 'delete'] },
      { resource: 'companies', actions: ['read', 'update'] },
      { resource: 'settings', actions: ['read', 'update'] },
      { resource: 'analytics', actions: ['read', 'export'] },
      { resource: 'funnel', actions: ['read', 'create', 'update', 'delete', 'export'] },
      { resource: 'sessions', actions: ['read', 'delete', 'export'] },
      { resource: 'attribution', actions: ['read', 'export'] },
      { resource: 'roas', actions: ['read', 'export'] },
      { resource: 'email-campaigns', actions: ['read', 'create', 'update', 'delete', 'send'] },
      { resource: 'email-logs', actions: ['read', 'export'] },
      { resource: 'getresponse', actions: ['read', 'sync', 'configure'] },
      { resource: 'contacts', actions: ['create', 'read', 'update', 'delete', 'export'] },
      { resource: 'eshop', actions: ['read', 'sync', 'configure'] },
      { resource: 'eshop-customers', actions: ['read', 'sync', 'export'] },
      { resource: 'data-sources', actions: ['create', 'read', 'update', 'delete', 'sync'] },
      { resource: 'betterstack', actions: ['read', 'sync', 'export', 'analyze'] },
      { resource: 'ai-insights', actions: ['read', 'generate'] },
      { resource: 'ai-chat', actions: ['read', 'query'] },
      { resource: 'dashboards', actions: ['create', 'read', 'update', 'delete'] },
      { resource: 'reports', actions: ['create', 'read', 'update', 'delete', 'export'] },
    ],
  },
  'marketing-manager': {
    name: 'Manager (Manage Users)',
    permissions: [
      { resource: 'analytics', actions: ['read', 'export'] },
      { resource: 'funnel', actions: ['read', 'export'] },
      { resource: 'sessions', actions: ['read', 'export'] },
      { resource: 'attribution', actions: ['read', 'export'] },
      { resource: 'roas', actions: ['read', 'export'] },
      { resource: 'email-campaigns', actions: ['read', 'create', 'update', 'delete', 'send'] },
      { resource: 'email-logs', actions: ['read', 'export'] },
      { resource: 'getresponse', actions: ['read', 'sync'] },
      { resource: 'contacts', actions: ['create', 'read', 'update', 'delete', 'export'] },
      { resource: 'eshop', actions: ['read', 'sync'] },
      { resource: 'eshop-customers', actions: ['read', 'sync', 'export'] },
      { resource: 'data-sources', actions: ['read'] },
      { resource: 'betterstack', actions: ['read', 'export'] },
      { resource: 'ai-insights', actions: ['read', 'generate'] },
      { resource: 'ai-chat', actions: ['read', 'query'] },
      { resource: 'dashboards', actions: ['read'] },
      { resource: 'reports', actions: ['read', 'export'] },
      { resource: 'profile', actions: ['read', 'update'] },
    ],
  },
  analyst: {
    name: 'Analyst (Read Analytics)',
    permissions: [
      { resource: 'analytics', actions: ['read', 'export'] },
      { resource: 'funnel', actions: ['read', 'export'] },
      { resource: 'sessions', actions: ['read', 'export'] },
      { resource: 'attribution', actions: ['read', 'export'] },
      { resource: 'roas', actions: ['read', 'export'] },
      { resource: 'email-campaigns', actions: ['read'] },
      { resource: 'email-logs', actions: ['read', 'export'] },
      { resource: 'eshop', actions: ['read'] },
      { resource: 'eshop-customers', actions: ['read', 'export'] },
      { resource: 'betterstack', actions: ['read', 'export'] },
      { resource: 'ai-insights', actions: ['read'] },
      { resource: 'ai-chat', actions: ['read', 'query'] },
      { resource: 'dashboards', actions: ['read'] },
      { resource: 'reports', actions: ['read', 'export'] },
      { resource: 'profile', actions: ['read', 'update'] },
    ],
  },
  viewer: {
    name: 'Viewer (Read Only)',
    permissions: [
      { resource: 'analytics', actions: ['read'] },
      { resource: 'sessions', actions: ['read'] },
      { resource: 'dashboards', actions: ['read'] },
      { resource: 'reports', actions: ['read'] },
      { resource: 'email-campaigns', actions: ['read'] },
      { resource: 'profile', actions: ['read', 'update'] },
    ],
  },
};

const RoleModal = ({ isOpen, onClose, role }: RoleModalProps) => {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const { selectedCompany } = useDataSourceStore();

  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    level: 50,
    defaultLandingPage: '/dashboard',
    permissions: [] as Permission[],
  });

  useEffect(() => {
    if (role) {
      setFormData({
        name: role.name,
        slug: role.slug,
        description: role.description || '',
        level: role.level,
        defaultLandingPage: role.defaultLandingPage || '/dashboard',
        permissions: role.permissions,
      });
    } else {
      setFormData({
        name: '',
        slug: '',
        description: '',
        level: 50,
        defaultLandingPage: '/dashboard',
        permissions: [],
      });
    }
  }, [role, isOpen]);

  const saveMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const companyId = selectedCompany?._id || user?.companyId;
      
      if (!companyId) {
        throw new Error('No company selected');
      }
      
      const payload = {
        ...data,
        companyId, // Add companyId from selected company or user
      };
      
      console.log('📤 Sending role payload to API:', payload);
      console.log('🎯 Payload defaultLandingPage:', payload.defaultLandingPage);
      
      if (role) {
        return api.put(`/api/v1/roles/${role._id}`, payload);
      } else {
        return api.post('/api/v1/roles', payload);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      toast.success(role ? 'Role updated successfully' : 'Role created successfully');
      onClose();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Failed to save role');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.permissions.length === 0) {
      toast.error('Please add at least one permission');
      return;
    }
    console.log('🔐 Saving role with data:', formData);
    console.log('🎯 Landing page being saved:', formData.defaultLandingPage);
    saveMutation.mutate(formData);
  };

  const handleNameChange = (name: string) => {
    setFormData({
      ...formData,
      name,
      slug: name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, ''),
    });
  };

  const hasPermission = (resource: string, action: string): boolean => {
    // Check for wildcard permissions
    if (formData.permissions.some(p => p.resource === '*' && p.actions.includes('*'))) {
      return true;
    }
    if (formData.permissions.some(p => p.resource === resource && p.actions.includes('*'))) {
      return true;
    }
    if (formData.permissions.some(p => p.resource === '*' && p.actions.includes(action))) {
      return true;
    }

    // Check for specific permission
    return formData.permissions.some(p => p.resource === resource && p.actions.includes(action));
  };

  const togglePermission = (resource: string, action: string) => {
    const has = hasPermission(resource, action);
    let newPermissions = [...formData.permissions];

    if (has) {
      // Remove permission
      newPermissions = newPermissions
        .map(p => {
          if (p.resource === resource) {
            return {
              ...p,
              actions: p.actions.filter(a => a !== action),
            };
          }
          return p;
        })
        .filter(p => p.actions.length > 0);
    } else {
      // Add permission
      const existingIndex = newPermissions.findIndex(p => p.resource === resource);
      if (existingIndex >= 0) {
        newPermissions[existingIndex].actions.push(action);
      } else {
        newPermissions.push({ resource, actions: [action] });
      }
    }

    setFormData({ ...formData, permissions: newPermissions });
  };

  const applyPreset = (presetKey: keyof typeof PERMISSION_PRESETS) => {
    const preset = PERMISSION_PRESETS[presetKey];
    setFormData({
      ...formData,
      permissions: preset.permissions,
    });
    toast.success(`Applied ${preset.name} preset`);
  };

  const clearAllPermissions = () => {
    setFormData({ ...formData, permissions: [] });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={role ? 'Edit Role' : 'New Role'} size="2xl">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Shield className="w-5 h-5 text-primary-600" />
            Basic Information
          </h3>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                Role Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={e => handleNameChange(e.target.value)}
                className="input"
                placeholder="Marketing Analyst"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">
                Slug <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.slug}
                onChange={e => setFormData({ ...formData, slug: e.target.value })}
                className="input"
                placeholder="marketing-analyst"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Description</label>
            <textarea
              value={formData.description}
              onChange={e => setFormData({ ...formData, description: e.target.value })}
              className="input"
              rows={2}
              placeholder="Brief description of this role's purpose..."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Level (0-100)</label>
              <input
                type="number"
                min="0"
                max="100"
                value={formData.level}
                onChange={e => setFormData({ ...formData, level: parseInt(e.target.value) })}
                className="input"
              />
              <p className="text-xs text-secondary-500 mt-1">
                Higher levels have more authority. Super Admin = 100
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Default Landing Page</label>
              <select
                value={formData.defaultLandingPage}
                onChange={e => setFormData({ ...formData, defaultLandingPage: e.target.value })}
                className="input"
              >
                <option value="/dashboard">Dashboard</option>
                <option value="/analytics">Analytics</option>
                <option value="/analytics/funnel">Funnel Analysis</option>
                <option value="/analytics/attribution">Attribution</option>
                <option value="/analytics/roas">ROAS Forecast</option>
                <option value="/sessions">Sessions</option>
                <option value="/email-campaigns">Email Campaigns</option>
                <option value="/email-logs">Email Logs</option>
                <option value="/eshops">E-shops</option>
                <option value="/betterstack">BetterStack</option>
              </select>
              <p className="text-xs text-secondary-500 mt-1">
                Page users will see after login
              </p>
            </div>
          </div>
        </div>

        {/* Permission Presets */}
        <div className="border-t pt-4">
          <h3 className="text-lg font-semibold flex items-center gap-2 mb-4">
            <Lock className="w-5 h-5 text-primary-600" />
            Quick Presets
          </h3>
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(PERMISSION_PRESETS).map(([key, preset]) => (
              <button
                key={key}
                type="button"
                onClick={() => applyPreset(key as keyof typeof PERMISSION_PRESETS)}
                className="btn-secondary text-sm"
              >
                {preset.name}
              </button>
            ))}
            <button
              type="button"
              onClick={clearAllPermissions}
              className="btn-secondary text-sm text-red-600 hover:text-red-700"
            >
              Clear All
            </button>
          </div>
        </div>

        {/* Permission Matrix */}
        <div className="border-t pt-4">
          <h3 className="text-lg font-semibold mb-4">Permission Matrix</h3>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-secondary-50 dark:bg-secondary-700">
                  <th className="border border-secondary-300 dark:border-secondary-600 p-2 text-left font-medium">
                    Resource
                  </th>
                  {ACTIONS.map(action => (
                    <th
                      key={action}
                      className="border border-secondary-300 dark:border-secondary-600 p-2 text-center font-medium capitalize"
                    >
                      {action}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {RESOURCES.map(resource => (
                  <tr key={resource} className="hover:bg-secondary-50 dark:hover:bg-secondary-800">
                    <td className="border border-secondary-300 dark:border-secondary-600 p-2 font-medium capitalize">
                      {resource === '*' ? 'All Resources' : resource}
                    </td>
                    {ACTIONS.map(action => (
                      <td
                        key={`${resource}-${action}`}
                        className="border border-secondary-300 dark:border-secondary-600 p-2 text-center"
                      >
                        <button
                          type="button"
                          onClick={() => togglePermission(resource, action)}
                          className={`w-8 h-8 rounded-md transition-all duration-200 flex items-center justify-center mx-auto ${
                            hasPermission(resource, action)
                              ? 'bg-primary-500 text-white hover:bg-primary-600'
                              : 'bg-secondary-200 dark:bg-secondary-700 hover:bg-secondary-300'
                          }`}
                        >
                          {hasPermission(resource, action) ? (
                            <Check className="w-5 h-5" />
                          ) : (
                            <X className="w-5 h-5 opacity-30" />
                          )}
                        </button>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {formData.permissions.length === 0 && (
            <p className="text-sm text-amber-600 mt-4">
              ⚠️ Please add at least one permission or apply a preset
            </p>
          )}
        </div>

        {/* Current Permissions Summary */}
        {formData.permissions.length > 0 && (
          <div className="border-t pt-4">
            <h3 className="text-sm font-semibold mb-2">Current Permissions:</h3>
            <div className="bg-secondary-50 dark:bg-secondary-800 p-3 rounded-lg font-mono text-xs">
              <pre className="whitespace-pre-wrap overflow-auto max-h-32">
                {JSON.stringify(formData.permissions, null, 2)}
              </pre>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <button type="button" onClick={onClose} className="btn btn-secondary">
            Cancel
          </button>
          <button
            type="submit"
            disabled={saveMutation.isPending || formData.permissions.length === 0}
            className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saveMutation.isPending ? 'Saving...' : role ? 'Update Role' : 'Create Role'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default RoleModal;
